<template>
  <Dialog :visible="manageSkillAssignmentsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ manageBotTitle }} Agent Skills
    </template>

    <div class="full-width flex">
      <Dropdown :options="availableSkills"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedSkill"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addSkillSubmitting" @click="addSkillToBot(selectedSkill)" :disabled="!selectedSkill" label="Add Agent Skill"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="botSkills">
        <Column field="name" header="Name"></Column>
        <Column field="shortDescription" header="Description"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="removeSkillFromBot(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeSkillSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageBotDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageBotId ? 'Edit' : 'Add' }} AI Agent
    </template>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <div class="form-element">
        Title:
        <InputText v-model="manageBotTitle" :invalid="manageBotTitleInvalid" class="form-element full-width" type="text"
                  @change="manageBotTitleChange"/>
        <div v-if="manageBotTitleInvalid">
          <InlineMessage severity="error">Title is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        User:
        <Dropdown v-model="manageBotUserId"
                  :options="userOptions"
                  option-label="label" option-value="id"
                  class="form-element full-width"
                  filter
                  :auto-filter-focus="true"
                  showClear
                  placeholder="None"
        />
      </div>

      <div class="form-element">
        User Group:
        <Dropdown v-model="manageBotUserGroupId"
                  :options="userGroupOptions"
                  option-label="label" option-value="id"
                  class="form-element full-width"
                  filter
                  :auto-filter-focus="true"
                  showClear
                  placeholder="None"
        />
      </div>

      <div class="form-element">
        Prompt:
        <Textarea v-model="manageBotPrompt" class="form-element full-width" rows="5" autoResize />
      </div>

      <div class="form-element">
        <div class="flex align-items-center">
          <Checkbox v-model="manageBotWebSearchEnabled" inputId="bot_web_search" :binary="true" />
          <label for="bot_web_search" class="ml-2">Web Search enabled</label>
        </div>
      </div>

      <div class="form-element">
        <label>Internal Knowledge:</label>
        <div class="vector-source-options">
          <div class="flex align-items-center">
            <RadioButton v-model="manageBotInternalKnowledgeMode" inputId="ik_all" value="all" />
            <label for="ik_all" class="ml-2">All Internal Knowledge</label>
          </div>
          <div class="vector-source-option">
            <div class="flex align-items-center">
              <RadioButton v-model="manageBotInternalKnowledgeMode" inputId="ik_select" value="select" />
              <label for="ik_select" class="ml-2">Select Internal Knowledge</label>
            </div>
            <div v-if="manageBotInternalKnowledgeMode === 'select'" class="ml-2 mt">
              <div class="vector-source-options">
                <div class="vector-source-option">
                  <div class="flex align-items-center">
                    <RadioButton v-model="botVectorSourceType" inputId="vs_files" value="files" />
                    <label for="vs_files" class="ml-2">Specific Files</label>
                  </div>
                  <div v-if="botVectorSourceType === 'files'" class="ml-2 mt">
                    <Button label="Select Files" icon="pi pi-folder-open" @click="openBotFileSelector" :disabled="loadingBotSelectedItems" class="p-button-sm mb-2" />
                    <ProgressSpinnerContainer v-if="loadingBotSelectedItems" style="min-height: 60px;" />
                    <div v-else-if="selectedBotFilesDisplay.length > 0" class="selected-items-display">
                      <small class="text-muted">Selected Files ({{ selectedBotFilesDisplay.length }}):</small>
                      <div class="selected-items-list">
                        <Chip v-for="file in selectedBotFilesDisplay" :key="file.id" :label="file.filename" removable @remove="removeSelectedBotFile(file.id)" />
                      </div>
                    </div>
                    <small v-else class="text-muted">No files selected</small>
                  </div>
                </div>
                <div class="vector-source-option">
                  <div class="flex align-items-center">
                    <RadioButton v-model="botVectorSourceType" inputId="vs_directories" value="directories" />
                    <label for="vs_directories" class="ml-2">Specific Directories</label>
                  </div>
                  <div v-if="botVectorSourceType === 'directories'" class="ml-2 mt">
                    <Button label="Select Directories" icon="pi pi-folder-open" @click="openBotDirectorySelector" :disabled="loadingBotSelectedItems" class="p-button-sm mb-2" />
                    <ProgressSpinnerContainer v-if="loadingBotSelectedItems" style="min-height: 60px;" />
                    <div v-else-if="selectedBotDirectoriesDisplay.length > 0" class="selected-items-display">
                      <small class="text-muted">Selected Directories ({{ selectedBotDirectoriesDisplay.length }}):</small>
                      <div class="selected-items-list">
                        <Chip v-for="dir in selectedBotDirectoriesDisplay" :key="dir.id" :label="dir.title" removable @remove="removeSelectedBotDirectory(dir.id)" />
                      </div>
                    </div>
                    <small v-else class="text-muted">No directories selected</small>
                  </div>
                </div>
                <div class="vector-source-option">
                  <div class="flex align-items-center">
                    <RadioButton v-model="botVectorSourceType" inputId="vs_knowledge_sources" value="knowledge_sources" />
                    <label for="vs_knowledge_sources" class="ml-2">Knowledge Sources</label>
                  </div>
                  <div v-if="botVectorSourceType === 'knowledge_sources'" class="ml-2 mt">
                    <MultiSelect v-model="botVectorSourceKnowledgeSourceIds"
                                :options="knowledgeSources"
                                option-label="name"
                                option-value="id"
                                placeholder="Select knowledge sources"
                                class="full-width"
                                display="chip"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex align-items-center">
            <RadioButton v-model="manageBotInternalKnowledgeMode" inputId="ik_none" value="none" />
            <label for="ik_none" class="ml-2">No Internal Knowledge</label>
          </div>
        </div>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageBotSubmitting" :loading="manageBotSubmitting" label="Save" type="button" @click="saveBot"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageBotUserGroupsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage User Groups
    </template>
    <div class="full-width flex">
      <Dropdown :options="availableGroupsForBot"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedGroupForBot"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addBotToGroupSubmitting" @click="addBotToGroup(selectedGroupForBot)" :disabled="!selectedGroupForBot" label="Add Group"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 35vh">
      <DataTable :value="botToManageUserGroups">
        <Column field="name" header="Name"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteBotUserGroup(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeBotFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageBotUsersDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage Users
    </template>
    <div class="full-width flex">
      <Dropdown :options="availableUsersForBot"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedUserForBot"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addBotToUserSubmitting" @click="addBotToUser(selectedUserForBot)" :disabled="!selectedUserForBot" label="Add User"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 35vh">
      <DataTable :value="botToManageUsers">
        <Column field="name" header="Name"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteBotUser(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeBotFromUserSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>

  <!-- Bot file selector (for Select Internal Knowledge > Specific Files) -->
  <Dialog v-model:visible="botFileSelectorDialogVisible"
          @hide="handleBotFileSelectorHide"
          header="Select Files"
          :style="{ width: '70vw' }"
          :modal="true"
          :closeOnEscape="true">
    <ScrollPanel style="height: 60vh">
      <FileTreeSelector
        :selectionMode="true"
        selectionType="files"
        v-model="tempBotSelectedFileIds"
        :preloadFileIds="botVectorSourceFileIds"
        @selection-change="onBotFileSelectionChange"
      />
    </ScrollPanel>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" @click="cancelBotFileSelection" class="p-button-text" />
      <Button label="OK" icon="pi pi-check" @click="confirmBotFileSelection" />
    </template>
  </Dialog>

  <!-- Bot directory selector (for Select Internal Knowledge > Specific Directories) -->
  <Dialog v-model:visible="botDirectorySelectorDialogVisible"
          @hide="handleBotDirectorySelectorHide"
          header="Select Directories"
          :style="{ width: '70vw' }"
          :modal="true"
          :closeOnEscape="true">
    <ScrollPanel style="height: 60vh">
      <FileTreeSelector
        :selectionMode="true"
        selectionType="directories"
        v-model="tempBotSelectedDirectoryIds"
        :preloadDirectoryIds="botVectorSourceDirectoryIds"
        @selection-change="onBotDirectorySelectionChange"
      />
    </ScrollPanel>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" @click="cancelBotDirectorySelection" class="p-button-text" />
      <Button label="OK" icon="pi pi-check" @click="confirmBotDirectorySelection" />
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="aiBotsLoading || skillsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">AI Agents</h2>
        <div class="align-right mr">
          <Button @click="manageBot(null)"><i class="pi pi-plus"></i> Add AI Agent</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="sortedBots">
          <Column field="title" header="Title"></Column>
          <Column header="User">
            <template #body="slotProps">
              {{ getUserName(slotProps.data.userId) }}
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="info" @click="manageBot(slotProps.data)" label="Edit"></Button>
              <Button class="ml" severity="info" @click="manageBotSkills(slotProps.data)" label="Manage Agent Skills"></Button>
              <Button class="ml" severity="info" @click="manageBotUserGroups(slotProps.data)" label="Manage Groups"></Button>
              <Button class="ml" severity="info" @click="manageBotUsers(slotProps.data)" label="Manage Users"></Button>
              <Button class="ml" severity="danger" @click="deleteBot(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import FileTreeSelector from "../components/FileTreeSelector.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { loadAiBots, useAiBots, useSkills, loadSkills, useUsers, useKnowledgeSources } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post, apiGetAll } from "../util/api.js";
import { decodeAiBot, decodeAiBotSkillAssignment, decodeAiBotAccessGrant, parseVectorSource } from "../util/util.js";

const { aiBots, aiBotSkillAssignments, aiBotAccessGrants, aiBotsLoading } = useAiBots()
const { skills, skillsLoading } = useSkills()
const { users, userGroups, hasAdminAccess } = useUsers()
const { knowledgeSources } = useKnowledgeSources()

const toast = useToast()
const confirm = useConfirm()

watch(selectedAccount, async (newValue) => {
  if (newValue) {
    await Promise.all([
      loadAiBots(),
      loadSkills()
    ])
  }
}, { immediate: true })

const manageBotDialogVisible = ref(false)
const manageBotSubmitting = ref(false)

const manageSkillAssignmentsDialogVisible = ref(false)
const addSkillSubmitting = ref(false)
const removeSkillSubmitting = ref(false)
const selectedSkill = ref(null)

const manageBotId = ref(null)
const manageBotTitle = ref(null)
const manageBotTitleInvalid = ref(false)
const manageBotUserId = ref(null)
const manageBotUserGroupId = ref(null)
const manageBotPrompt = ref('')
const manageBotWebSearchEnabled = ref(false)
const manageBotInternalKnowledgeMode = ref('none')
// Vector source state when "Select Internal Knowledge" is chosen (same options as Skills.vue Vector Query)
const botVectorSourceType = ref('all')
const botVectorSourceFileIndex = ref(0)
const botVectorSourceFileIds = ref([])
const botVectorSourceDirectoryIds = ref([])
const botVectorSourceKnowledgeSourceIds = ref([])
const botFileSelectorDialogVisible = ref(false)
const botDirectorySelectorDialogVisible = ref(false)
const tempBotSelectedFileIds = ref([])
const tempBotSelectedDirectoryIds = ref([])
const tempBotSelectedFiles = ref([])
const tempBotSelectedDirectories = ref([])
const selectedBotFiles = ref([])
const selectedBotDirectories = ref([])
const loadingBotSelectedItems = ref(false)

function manageBotTitleChange() {
  manageBotTitleInvalid.value = !manageBotTitle.value
}

function buildBotVectorSource() {
  switch (botVectorSourceType.value) {
    case 'all':
      return 'all'
    case 'input_files':
      return 'input_files'
    case 'input_file_index':
      return `input_file[${botVectorSourceFileIndex.value ?? 0}]`
    case 'files': {
      const fileIds = botVectorSourceFileIds.value
        .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
        .join(',')
      return fileIds
    }
    case 'directories': {
      const dirIds = botVectorSourceDirectoryIds.value
        .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
        .join(',')
      return dirIds ? `directory_id: ${dirIds}` : ''
    }
    case 'knowledge_sources': {
      const ksIds = botVectorSourceKnowledgeSourceIds.value
        .filter(id => id !== null && id !== undefined)
        .join(',')
      return ksIds ? `knowledge_source_id: ${ksIds}` : ''
    }
    default:
      return 'all'
  }
}

const selectedBotFilesDisplay = computed(() => selectedBotFiles.value)
const selectedBotDirectoriesDisplay = computed(() => selectedBotDirectories.value)

function openBotFileSelector() {
  tempBotSelectedFileIds.value = [...botVectorSourceFileIds.value]
  botFileSelectorDialogVisible.value = true
}

function onBotFileSelectionChange(selection) {
  tempBotSelectedFiles.value = selection
}

function cancelBotFileSelection() {
  tempBotSelectedFileIds.value = []
  tempBotSelectedFiles.value = []
  botFileSelectorDialogVisible.value = false
}

function confirmBotFileSelection() {
  botVectorSourceFileIds.value = [...tempBotSelectedFileIds.value]
  selectedBotFiles.value = [...tempBotSelectedFiles.value]
  tempBotSelectedFileIds.value = []
  tempBotSelectedFiles.value = []
  botFileSelectorDialogVisible.value = false
}

function handleBotFileSelectorHide() {
  tempBotSelectedFileIds.value = []
  tempBotSelectedFiles.value = []
}

function removeSelectedBotFile(fileId) {
  botVectorSourceFileIds.value = botVectorSourceFileIds.value.filter(id => String(id) !== String(fileId))
  selectedBotFiles.value = selectedBotFiles.value.filter(file => String(file.id) !== String(fileId))
}

function openBotDirectorySelector() {
  tempBotSelectedDirectoryIds.value = [...botVectorSourceDirectoryIds.value]
  botDirectorySelectorDialogVisible.value = true
}

function onBotDirectorySelectionChange(selection) {
  tempBotSelectedDirectories.value = selection
}

function cancelBotDirectorySelection() {
  tempBotSelectedDirectoryIds.value = []
  tempBotSelectedDirectories.value = []
  botDirectorySelectorDialogVisible.value = false
}

function confirmBotDirectorySelection() {
  botVectorSourceDirectoryIds.value = [...tempBotSelectedDirectoryIds.value]
  selectedBotDirectories.value = [...tempBotSelectedDirectories.value]
  tempBotSelectedDirectoryIds.value = []
  tempBotSelectedDirectories.value = []
  botDirectorySelectorDialogVisible.value = false
}

function handleBotDirectorySelectorHide() {
  tempBotSelectedDirectoryIds.value = []
  tempBotSelectedDirectories.value = []
}

function removeSelectedBotDirectory(directoryId) {
  botVectorSourceDirectoryIds.value = botVectorSourceDirectoryIds.value.filter(id => String(id) !== String(directoryId))
  selectedBotDirectories.value = selectedBotDirectories.value.filter(dir => String(dir.id) !== String(directoryId))
}

async function loadSelectedBotFilesAndDirectories() {
  loadingBotSelectedItems.value = true
  try {
    if (botVectorSourceFileIds.value.length > 0) {
      try {
        const fileIdsStr = botVectorSourceFileIds.value.join(',')
        const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${fileIdsStr}]}}`
        const files = await apiGetAll(`/v1/files${query}`)
        selectedBotFiles.value = files
      } catch (error) {
        console.error('Error loading selected files:', error)
        selectedBotFiles.value = []
      }
    } else {
      selectedBotFiles.value = []
    }
    if (botVectorSourceDirectoryIds.value.length > 0) {
      try {
        const dirIdsStr = botVectorSourceDirectoryIds.value.join(',')
        const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${dirIdsStr}]}}`
        const directories = await apiGetAll(`/v1/file-directories${query}`)
        selectedBotDirectories.value = directories
      } catch (error) {
        console.error('Error loading selected directories:', error)
        selectedBotDirectories.value = []
      }
    } else {
      selectedBotDirectories.value = []
    }
  } finally {
    loadingBotSelectedItems.value = false
  }
}

function manageBot(data) {
  manageBotDialogVisible.value = true
  manageBotId.value = data?.id
  manageBotTitle.value = data?.title
  manageBotUserId.value = data?.userId || null
  manageBotUserGroupId.value = data?.userGroupId || null
  manageBotPrompt.value = data?.metadata?.prompt ?? ''
  manageBotWebSearchEnabled.value = data?.webSearchEnabled ?? false
  manageBotInternalKnowledgeMode.value = data?.internalKnowledgeMode ?? 'none'

  const rawVectorSource = data?.internalKnowledgeVectorSource ?? ''
  const parsed = parseVectorSource(rawVectorSource)
  botVectorSourceType.value = parsed.type
  botVectorSourceFileIndex.value = parsed.fileIndex
  botVectorSourceFileIds.value = [...(parsed.fileIds || [])]
  botVectorSourceDirectoryIds.value = [...(parsed.directoryIds || [])]
  botVectorSourceKnowledgeSourceIds.value = [...(parsed.knowledgeSourceIds || [])]
  selectedBotFiles.value = []
  selectedBotDirectories.value = []

  if (manageBotInternalKnowledgeMode.value === 'select') {
    loadSelectedBotFilesAndDirectories()
  }
}

function manageBotSkills(data) {
  manageSkillAssignmentsDialogVisible.value = true
  manageBotId.value = data?.id
  manageBotTitle.value = data?.title
}

const botToManage = ref(null)

const selectedGroupForBot = ref(null)
const addBotToGroupSubmitting = ref(false)
const removeBotFromGroupSubmitting = ref(false)
const manageBotUserGroupsDialogVisible = ref(false)

const botToManageUserGroups = computed(() => {
  const output = []
  if (!botToManage.value) {
    return output
  }
  for (let i = 0; i < aiBotAccessGrants.value.length; i++) {
    if (String(aiBotAccessGrants.value[i].botId) === String(botToManage.value.id)) {
      const group = userGroups.value.find(g => String(g.id) === String(aiBotAccessGrants.value[i].groupId))
      if (group) {
        output.push({
          botAccessGrantId: aiBotAccessGrants.value[i].id,
          ...group
        })
      }
    }
  }
  return output
})

const availableGroupsForBot = computed(() => userGroups.value.filter(g => !botToManageUserGroups.value?.find(a => String(a.groupId) === String(g.id))))

async function addBotToGroup(id) {
  const payload = {
    ai_bots_id: botToManage.value.id,
    cephisso_platform_user_groups_id: id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addBotToGroupSubmitting.value = true
  const request = await post('/crbn-tech/ai-bot-access-grants', payload)
  if (request.id) {
    aiBotAccessGrants.value.push(decodeAiBotAccessGrant(request))
  }
  else {
    handleError()
  }
  addBotToGroupSubmitting.value = false
  selectedGroupForBot.value = null
}

function deleteBotUserGroup(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + botToManage.value.title + ' from ' + data.name + '?',
    header: 'Confirm User Group Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeBotFromGroupSubmitting.value = true
        for (let i = 0; i < aiBotAccessGrants.value?.length; i++) {
          if (
              (String(aiBotAccessGrants.value[i].groupId) === String(data.id)) &&
              (String(aiBotAccessGrants.value[i].botId) === String(botToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-bot-access-grants/' + aiBotAccessGrants.value[i].id)
            if (request.id) {
              aiBotAccessGrants.value = aiBotAccessGrants.value.filter(sa => String(sa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeBotFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

const selectedUserForBot = ref(null)
const addBotToUserSubmitting = ref(false)
const removeBotFromUserSubmitting = ref(false)
const manageBotUsersDialogVisible = ref(false)

const botToManageUsers = computed(() => {
  const output = []
  if (!botToManage.value) {
    return output
  }
  for (let i = 0; i < aiBotAccessGrants.value.length; i++) {
    if (String(aiBotAccessGrants.value[i].botId) === String(botToManage.value.id)) {
      const user = users.value.find(u => String(u.id) === String(aiBotAccessGrants.value[i].userId))
      if (user) {
        output.push({
          botAccessGrantId: aiBotAccessGrants.value[i].id,
          ...user
        })
      }
    }
  }
  return output
})

const availableUsersForBot = computed(() => users.value.filter(u => !botToManageUsers.value?.find(a => String(a.userId) === String(u.id))))

async function addBotToUser(id) {
  const payload = {
    ai_bots_id: botToManage.value.id,
    cephisso_platform_users_id: id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addBotToUserSubmitting.value = true
  const request = await post('/crbn-tech/ai-bot-access-grants', payload)
  if (request.id) {
    aiBotAccessGrants.value.push(decodeAiBotAccessGrant(request))
  }
  else {
    handleError()
  }
  addBotToUserSubmitting.value = false
  selectedUserForBot.value = null
}

function deleteBotUser(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + botToManage.value.title + ' from ' + data.name + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeBotFromUserSubmitting.value = true
        for (let i = 0; i < aiBotAccessGrants.value?.length; i++) {
          if (
              (String(aiBotAccessGrants.value[i].userId) === String(data.id)) &&
              (String(aiBotAccessGrants.value[i].botId) === String(botToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-bot-access-grants/' + aiBotAccessGrants.value[i].id)
            if (request.id) {
              aiBotAccessGrants.value = aiBotAccessGrants.value.filter(sa => String(sa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeBotFromUserSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

function manageBotUserGroups(bot) {
  botToManage.value = {
    id: bot.id,
    title: bot.title
  }
  manageBotUserGroupsDialogVisible.value = true
}

function manageBotUsers(bot) {
  botToManage.value = {
    id: bot.id,
    title: bot.title
  }
  manageBotUsersDialogVisible.value = true
}

function handleClose() {
  manageBotId.value = null
  manageBotTitle.value = null
  manageBotTitleInvalid.value = false
  manageBotUserId.value = null
  manageBotUserGroupId.value = null
  manageBotPrompt.value = ''
  manageBotWebSearchEnabled.value = false
  manageBotInternalKnowledgeMode.value = 'none'
  botVectorSourceType.value = 'all'
  botVectorSourceFileIndex.value = 0
  botVectorSourceFileIds.value = []
  botVectorSourceDirectoryIds.value = []
  botVectorSourceKnowledgeSourceIds.value = []
  selectedBotFiles.value = []
  selectedBotDirectories.value = []
  loadingBotSelectedItems.value = false
  manageBotSubmitting.value = false
  manageBotDialogVisible.value = false

  manageSkillAssignmentsDialogVisible.value = false
  addSkillSubmitting.value = false
  removeSkillSubmitting.value = false
  selectedSkill.value = null

  botToManage.value = null
  selectedGroupForBot.value = null
  selectedUserForBot.value = null
  addBotToGroupSubmitting.value = false
  removeBotFromGroupSubmitting.value = false
  manageBotUserGroupsDialogVisible.value = false
  addBotToUserSubmitting.value = false
  removeBotFromUserSubmitting.value = false
  manageBotUsersDialogVisible.value = false
}

function handleError(e) {
  console.log(e)
  addSkillSubmitting.value = false
  manageBotSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

async function saveBot() {
  manageBotTitleInvalid.value = false
  if (!manageBotTitle.value) {
    manageBotTitleInvalid.value = true
    return false
  }

  if (manageBotId.value) {
    const existingBot = aiBots.value.find(b => String(b.id) === String(manageBotId.value))
    const metadata = { ...(existingBot?.metadata || {}) }
    metadata.prompt = manageBotPrompt.value || null
    metadata.webSearchEnabled = manageBotWebSearchEnabled.value
    metadata.internalKnowledge = manageBotInternalKnowledgeMode.value === 'none' ? null : manageBotInternalKnowledgeMode.value
    const builtVectorSource = manageBotInternalKnowledgeMode.value === 'select' ? buildBotVectorSource() : null
    metadata.internalKnowledgeVectorSource = (builtVectorSource && String(builtVectorSource).trim() !== '') ? builtVectorSource : null

    const payload = {
      title: manageBotTitle.value,
      cephisso_platform_users_id: manageBotUserId.value || null,
      cephisso_platform_user_groups_id: manageBotUserGroupId.value || null,
      metadata: JSON.stringify(metadata),
    }
    manageBotSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/ai-bots/' + manageBotId.value, payload)
      if (result.id) {
        for (let i = 0; i < aiBots.value.length; i++) {
          if (String(aiBots.value[i].id) === String(result.id)) {
            aiBots.value[i] = decodeAiBot(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageBotSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const metadata = {}
    metadata.prompt = manageBotPrompt.value || null
    metadata.webSearchEnabled = manageBotWebSearchEnabled.value
    metadata.internalKnowledge = manageBotInternalKnowledgeMode.value === 'none' ? null : manageBotInternalKnowledgeMode.value
    const builtVectorSourceNew = manageBotInternalKnowledgeMode.value === 'select' ? buildBotVectorSource() : null
    metadata.internalKnowledgeVectorSource = (builtVectorSourceNew && String(builtVectorSourceNew).trim() !== '') ? builtVectorSourceNew : null

    const payload = {
      title: manageBotTitle.value,
      cephisso_platform_users_id: manageBotUserId.value || null,
      cephisso_platform_user_groups_id: manageBotUserGroupId.value || null,
      cephisso_platform_accounts_id: selectedAccount.value,
      metadata: JSON.stringify(metadata),
    }
    manageBotSubmitting.value = true
    try {
      const result = await post('/crbn-tech/ai-bots', payload)
      if (result.id) {
        aiBots.value.push(decodeAiBot(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageBotSubmitting.value = false
      handleError(e)
    }
  }
}

async function deleteBot(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.title + '?',
    header: 'Confirm Delete AI Agent',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        aiBotsLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-bots/' + data.id)
        if (request.id) {
          aiBots.value = aiBots.value.filter(q => String(q.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        aiBotsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

function getUserName(userId) {
  if (!userId) return ''
  const user = users.value.find(u => String(u.id) === String(userId))
  return user ? (user.name?.trim() || user.email || '') : ''
}

const userOptions = computed(() => {
  return users.value.map(u => ({
    id: u.id,
    label: (u.name?.trim() || '') + (u.email ? ' (' + u.email + ')' : '')
  })).sort((a, b) => a.label.localeCompare(b.label))
})

const userGroupOptions = computed(() => {
  return userGroups.value.map(g => ({
    id: g.id,
    label: g.name || ''
  })).sort((a, b) => a.label.localeCompare(b.label))
})

const sortedBots = computed(() => {
  return aiBots.value.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''))
})

const botSkills = computed(() => {
  const output = []
  if (!manageBotId.value) {
    return output
  }
  for (let i = 0; i < aiBotSkillAssignments.value.length; i++) {
    if (String(aiBotSkillAssignments.value[i].aiBotId) === String(manageBotId.value)) {
      const skill = skills.value.find(s => String(s.id) === String(aiBotSkillAssignments.value[i].skillId))
      if (skill) {
        output.push({
          assignmentId: aiBotSkillAssignments.value[i].id,
          ...skill
        })
      }
    }
  }
  return output
})

const availableSkills = computed(() => skills.value.filter(s => !botSkills.value?.find(a => String(a.id) === String(s.id))))

async function addSkillToBot(id) {
  const payload = {
    ai_skills_id: id,
    ai_bots_id: manageBotId.value,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addSkillSubmitting.value = true
  try {
    const request = await post('/crbn-tech/ai-bots-skill-assignment', payload)
    if (request.id) {
      aiBotSkillAssignments.value.push(decodeAiBotSkillAssignment(request))
    }
    else {
      handleError()
    }
  }
  catch (e) {
    handleError(e)
  }
  addSkillSubmitting.value = false
  selectedSkill.value = null
}

async function removeSkillFromBot(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + manageBotTitle.value + '?',
    header: 'Confirm Agent Skill Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeSkillSubmitting.value = true
        const request = await deleteRequest('/crbn-tech/ai-bots-skill-assignment/' + data.assignmentId)
        if (request.id) {
          aiBotSkillAssignments.value = aiBotSkillAssignments.value.filter(a => String(a.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      removeSkillSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}
</script>

<style scoped>
.vector-source-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
}

.vector-source-option {
  display: flex;
  flex-direction: column;
}

.selected-items-display {
  margin-top: 0.5rem;
}

.selected-items-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style>
